import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

import { Breadcrumb } from "../../../components/common/u1/Breadcrumb";
import { ProfileHero } from "../../../components/public/Company/u1/ProfileHero";
import { ProfileTabs, PROFILE_TABS } from "../../../components/public/Company/u1/ProfileTabs";
import { GeneralInformation } from "../../../components/public/Company/u1/GeneralInformation";
import { BusinessDetails } from "../../../components/public/Company/u1/BusinessDetails";
import { FinancialsTab } from "../../../components/public/Company/u1/FinancialsTab";
import { DocumentsTab } from "../../../components/public/Company/u1/DocumentsTab";
import { GetTheBusinessGate } from "../../../components/public/Company/u1/GetTheBusinessGate";
import {
  GatedTabPreview,
  GatedTabPrompt,
} from "../../../components/public/Company/u1/GatedTabPreview";
import { WIDE_MEASURE } from "../../../components/common/u1/measure";
import { useCompanyProfile } from "../../../components/public/Company/u1/useCompanyProfile";
import {
  useProfileAccess,
  canOpenGeneralInfo,
  generalInfoOfferable,
  showDocumentsTab,
} from "../../../components/public/Company/u1/profileAccess";
import {
  ProfileSocials,
  ProfileMoreMenu,
  VerifiedByTrevex,
} from "../../../components/public/Company/u1/ProfileActions";
import { ProfileActionBar } from "../../../components/public/Company/u1/ProfileActionBar";
import { ProfileOwnerCta } from "../../../components/public/Company/u1/ProfileOwnerCta";
import { useProfileActions } from "../../../components/public/Company/u1/useProfileActions";
import { usePurchaseStatus } from "../../../components/public/Company/u1/usePurchaseStatus";
import { useOwnerCta } from "../../../components/public/Company/u1/useOwnerCta";
import { useEndorsements } from "../../../components/public/Company/u1/useEndorsements";
import EndorsementBanner from "../../../components/common/badges/EndorsementBanner";
import { useGetTheBusiness } from "../../../components/public/Company/u1/useGetTheBusiness";
import { useProfileExport } from "../../../components/public/Company/u1/useProfileExport";
import { ManageRolesModal } from "../../../components/public/Company/u1/ManageRolesModal";
import { CommonToast } from "../../../components/common/Toast";
import "../../../components/common/u1/tokens.css";

/* u1 Company Profile — now serving /search/:id. */
export default function CompanyProfile() {
  const {
    id,
    company,
    trevexId,
    role,
    purchaseSubScriptionValid,
    refreshPurchase,
    isLoading,
  } = useCompanyProfile();
  const [activeTab, setActiveTab] = useState(1);
  /* Transcribed: the live screen shows the offer as soon as a gated tab is
     opened, rather than waiting for a click. Dismissing it leaves the locked
     panel behind, which can reopen it; changing tab offers it again. */
  const [offerDismissed, setOfferDismissed] = useState(false);
  const [rolesOpen, setRolesOpen] = useState(false);
  /* Read here rather than through another hook's return: the More control is
     hidden outright for a guest, and routing the session user through the
     endorsements hook made that dependency invisible at the call site. */
  const { userInfo } = useSelector((state) => state.auth);
  const navigate = useNavigate();

  /* The gate for tabs 02-04 — see profileAccess.js. */
  const { canOpen } = useProfileAccess({
    company,
    trevexId,
    role,
    purchaseSubScriptionValid,
  });
  /* Transcribed: the Documents tab only exists for a record that is not a bare
     TREVEX one, or is verified, or is claimed — three tabs otherwise. */
  const tabs = PROFILE_TABS.filter((t) => t.id !== 3 || showDocumentsTab(company));

  /* Two different gates. Tab 01 has its own — the real General Information shows only for a claimed company or a live purchase — which is why a listed company... */
  const generalOpen = canOpenGeneralInfo(company, purchaseSubScriptionValid);
  const isLocked = (tab) => (tab === 1 ? !generalOpen : !canOpen);

  /* Transcribed: on tab 01 the offer only appears for a listing or a draft —
     any other locked record renders nothing there on the live screen. A locked
     tab 02-04 always offers. */
  const offerable = (tab) => (tab === 1 ? generalInfoOfferable(company) : true);
  const showTab = (tab) => {
    setActiveTab(tab);
    /* Dismissing the offer used to last only until the next tab, which put the card back in front of someone who had just closed it — and now that the gated tabs... */
  };

  /* The flattened purchase details, the mapped statusData and the WorldBox
     flag — what the enterprise button and the subscription pill read. */
  const purchase = usePurchaseStatus(company);

  /* Follow, unfollow, claim and the flags the action bar reads. */
  const actions = useProfileActions({
    company,
    trevexId,
    role,
    purchaseSubScriptionValid,
  });

  /* The owner's own chain — Get Membership, Get Verified, and the two
     in-progress states. Transcribed; see ownerCta.js. */
  const owner = useOwnerCta({ company, role });

  /* The endorsement banner, transcribed with its gate. The banner itself is
     the shared component the live screen uses — it composes EndorsementsBadge
     and EndorsementButton, both of which already ship. */
  const endorsements = useEndorsements({ company, role });

  /* Buy Full Profile. Transcribed from handleVerifyProfile:
       (verifiedCompany || claimed) ? handleGetCompany() : createWorldBoxOrder()
     Both handlers are the transcribed ones. */
  const business = useGetTheBusiness({ company, trevexId });
  /* The offer's dialogs are rendered by GetTheBusinessGate, which now runs on THIS instance (see the note there). */
  const flowDialogOpen = business.getCompanyPopup || business.creditNoticePopup;
  /* A spent credit changes both reads the gates gate on: the page's purchase
     window (tab 01 and tabs 02-04) and the masthead's purchase status. */
  const onPurchased = () => {
    refreshPurchase();
    purchase.refresh();
  };
  const buyFullProfile = () => {
    const verified = company?.verifiedCompany != null && Object.keys(company.verifiedCompany || {}).length !== 0;
    return verified || company?.claimed
      ? business.handleGetCompany()
      : business.createWorldBoxOrder();
  };

  /* Sharing needs nothing but the current URL, so it is done here. The live
     screen opens a modal of share targets; this copies the link, which is the
     one thing every target needs. */
  const shareProfile = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      CommonToast.success("Profile link copied.");
    } catch {
      /* Clipboard access is refused in some browsers and over plain http.
         Said out loud rather than failing silently. */
      CommonToast.error("Couldn't copy the link. Copy it from the address bar.");
    }
  };

  /* All three More options now work. Save as PDF is the transcribed export
     (useProfileExport), Manage Roles the transcribed modal. */
  const { exportPdf } = useProfileExport(company);

  const onMoreOption = (option) => {
    if (option === "Share Profile") return shareProfile();
    if (option === "Save Business as PDF") return exportPdf();
    if (option === "Manage Roles") return setRolesOpen(true);
    return undefined;
  };

  const crumbs = [
    { label: "Home", to: "/" },
    { label: "Search Result", to: "/search" },
    { label: id || "Company" },
  ];

  /* The content bound, widened for this screen only — see WIDE_MEASURE. */
  return (
    <div className={`u1-root ${WIDE_MEASURE} py-5`}>
      <Breadcrumb items={crumbs} />

      {isLoading && !company ? (
        /* The shared ResultSkeleton announces "Searching the directory…",
           which is not what is happening here, so this page says its own
           thing. */
        <div
          role="status"
          aria-live="polite"
          aria-busy="true"
          className="mt-4 rounded-[16px] border border-[var(--line)] bg-white p-6"
        >
          <span className="sr-only">Loading this company profile…</span>
          <div aria-hidden="true" className="flex flex-col gap-3">
            <span className="h-[100px] w-full rounded-xl bg-[var(--skeleton-hi)]" />
            <span className="h-6 w-[240px] rounded bg-[var(--skeleton-hi)]" />
            <span className="h-4 w-[320px] rounded bg-[var(--skeleton-hi)]" />
          </div>
        </div>
      ) : (
        <>
          <ProfileHero
            className="mt-4"
            company={company}
            trevexId={trevexId}
            socials={<ProfileSocials company={company} onShare={shareProfile} />}
            actions={
              <div
                data-testid="profile-actions-row"
                className="flex flex-wrap items-center justify-between gap-3"
              >
                <ProfileMoreMenu
                  id={id}
                  role={role}
                  purchaseSubScriptionValid={purchaseSubScriptionValid}
                  /* Hidden outright for a guest — see moreMenuHidden. */
                  userInfo={userInfo}
                  onSelect={onMoreOption}
                />
                {/* The owner's CTA sits on this row rather than in a card of its own — the design puts Get Membership / Get Verified opposite More, the same place a listed... */}
                <div className="flex flex-wrap items-center justify-end gap-3">
                  <VerifiedByTrevex company={company} />
                  <ProfileOwnerCta
                    kind={owner?.kind}
                    role={role}
                    submitting={owner?.submitting}
                    onGetVerified={owner?.getVerified}
                    onGetMembership={owner?.getMembership}
                  />
                </div>
              </div>
            }
          >
            <ProfileActionBar
              company={company}
              role={role}
              actions={actions}
              purchase={purchase}
              owner={owner}
              onBuyFullProfile={buyFullProfile}
            />
          </ProfileHero>

          {/* Transcribed gate (BannerCompanyCard:1715): a verified company only
              for a viewer with no role on it, a claimed company for anyone. */}
          {endorsements.visible && (
            <div className="mt-3 overflow-hidden rounded-[14px]">
              <EndorsementBanner
                company={company}
                role={role}
                companyType={endorsements.companyType}
                userInfo={endorsements.userInfo}
                trevexId={trevexId}
                endorsementMode={endorsements.endorsementMode}
                endorsementCount={endorsements.endorsementCount}
              />
            </div>
          )}

          <div className="mt-[12px]">
            <ProfileTabs
              tabs={tabs}
              active={activeTab}
              onChange={showTab}
              isLocked={isLocked}
            />

            <div
              role="tabpanel"
              id={`u1-panel-${activeTab}`}
              aria-labelledby={`u1-tab-${activeTab}`}
              className="rounded-b-[14px] border border-t-0 border-[var(--line)] bg-[var(--bg)] p-4 sm:p-6"
            >
              {/* A locked tab 02-04 renders the offer, never the content — the content's own requests would 403. */}
              {isLocked(activeTab) && activeTab === 1 && offerable(1) ? (
                /* A FIXED height, clipped — not min-h. */
                <div className="relative h-[520px] overflow-hidden">
                  <div
                    aria-hidden="true"
                    className="pointer-events-none select-none blur-[4px]"
                    data-testid="u1-blurred-content"
                  >
                    <GatedTabPreview tab={1} />
                  </div>
                  {/* The clip, softened — the preview fades out rather than
                      ending on a hard edge. Inert and below the offer. */}
                  <div
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-x-0 bottom-0 z-0 h-28 bg-gradient-to-b from-transparent to-[var(--bg)]"
                  />
                  {/* Dismissed: the blurred content stays and this is the way
                      back, matching the gated tabs below. */}
                  {offerDismissed && !flowDialogOpen && (
                    <GatedTabPrompt
                      className="absolute inset-x-0 bottom-0 z-[2]"
                      onGetDetails={() => setOfferDismissed(false)}
                    />
                  )}
                  {(!offerDismissed || flowDialogOpen) && (
                    <GetTheBusinessGate
                      variant="inline"
                      company={company}
                      trevexId={trevexId}
                      onClose={() => setOfferDismissed(true)}
                      isFollowing={actions.isFollowing}
                      flow={business}
                      onPurchased={onPurchased}
                      /* The overlay below owns the flow's dialogs. Both
                         instances share one flow, so without this both draw
                         the same modal — see the note on the prop. */
                      ownsFlowDialogs={false}
                    />
                  )}
                </div>
              ) : isLocked(activeTab) && offerable(activeTab) ? (
                /* A gated tab 02-04, shaped exactly like tab 01 above: its own placeholder blurred, with the offer floated over it, in the same fixed, clipped, non-scrolling... */
                <div className="relative h-[520px] overflow-hidden">
                  <div
                    aria-hidden="true"
                    className="pointer-events-none select-none blur-[4px]"
                    data-testid="u1-blurred-content"
                  >
                    <GatedTabPreview tab={activeTab} />
                  </div>
                  {/* The clip, softened — the preview fades out rather than
                      ending on a hard edge. Inert and below the offer. */}
                  <div
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-x-0 bottom-0 z-0 h-28 bg-gradient-to-b from-transparent to-[var(--bg)]"
                  />

                  {/* The offer is the inline CARD floated over the blur — the same one tab 01 uses, and what the live screen shows. */}
                  {!offerDismissed ? (
                    <GetTheBusinessGate
                      variant="inline"
                      company={company}
                      trevexId={trevexId}
                      onClose={() => setOfferDismissed(true)}
                      isFollowing={actions.isFollowing}
                      flow={business}
                      onPurchased={onPurchased}
                      /* The page-level instance owns the flow's dialogs. */
                      ownsFlowDialogs={false}
                    />
                  ) : (
                    /* Closed on purpose: the preview and the tab strip stay
                       browsable, with one unblurred way back so nobody is
                       stranded on a blurred panel with nothing to click. */
                    <GatedTabPrompt
                      className="absolute inset-x-0 bottom-0 z-[2]"
                      onGetDetails={() => setOfferDismissed(false)}
                    />
                  )}
                </div>
              ) : isLocked(activeTab) ? (
                /* Locked with nothing to offer — a record that is neither
                   claimed nor a draft. The live chain renders nothing here
                   rather than offering something that cannot be bought. */
                null
              ) : (
                <>
                  {activeTab === 1 && (
                    <GeneralInformation company={company} trevexId={trevexId} />
                  )}

                  {activeTab === 2 && (
                    <BusinessDetails
                      company={company}
                      /* Transcribed: a related company on TREVEX opens its own
                         profile in-app. */
                      onOpenCompany={(companyID) => navigate(`/search/${companyID}`)}
                    />
                  )}

                  {/* Tab 4 is Financials, tab 3 is Documents — the live
                      numbering, which the design shows as 03 and 04. */}
                  {activeTab === 4 && <FinancialsTab company={company} companyId={id} />}
                  {activeTab === 3 && <DocumentsTab company={company} />}
                </>
              )}
            </div>
          </div>

          {rolesOpen && <ManageRolesModal onClose={() => setRolesOpen(false)} />}

          {/* Only the flow's dialogs now. Both gated tabs and tab 01 render
              their own inline offer card, so a page-level offer on top of one
              would be the same card twice. */}
          {flowDialogOpen && (
            <GetTheBusinessGate
              company={company}
              trevexId={trevexId}
              onClose={() => setOfferDismissed(true)}
              flow={business}
              /* The credit spend follows the company only when it is not
                 already followed — see useSpendCredit. */
              isFollowing={actions.isFollowing}
              onPurchased={onPurchased}
            />
          )}
        </>
      )}
    </div>
  );
}
